import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchVendors } from '../../redux/actions/vendorActions';
import { Button, Table, message, Input, Tag, Tooltip, Modal, DatePicker, Space, Typography } from 'antd';
import { TrophyOutlined, FileTextOutlined, DownloadOutlined, EyeOutlined, TeamOutlined, BankOutlined, CheckOutlined, LockOutlined, EditOutlined, PlusOutlined, PhoneOutlined, UserOutlined, FileProtectOutlined } from '@ant-design/icons';
import { hasPermission } from '../../utils/permissions';
import VendorPerformanceDetail, { getGradeTag } from './VendorPerformanceDetail';
import { fetchVendorPerformanceRequest } from '../../redux/actions/vendorPerformanceActions';
import { vendorStatementAPI } from '../../api/config';
import dayjs from 'dayjs';
import './Vendor.css';

const { Search } = Input;

const VendorList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { vendors, loading, error } = useSelector((state) => state.vendors);
  const permissions = useSelector(state => state.auth.permissions || []);
  const [searchText, setSearchText] = useState('');
  const [perfVendor, setPerfVendor] = useState(null);
  const [stmtVendor, setStmtVendor] = useState(null); // { id, firm_name }
  const [stmtDateRange, setStmtDateRange] = useState(null);
  const [stmtLoading, setStmtLoading] = useState(false);

  const handleDownloadStatement = async (format) => {
    if (!stmtVendor) return;
    setStmtLoading(true);
    try {
      const params = { format };
      if (stmtDateRange?.[0]) params.date_from = stmtDateRange[0].format('YYYY-MM-DD');
      if (stmtDateRange?.[1]) params.date_to   = stmtDateRange[1].format('YYYY-MM-DD');
      const res = await vendorStatementAPI.getStatement(stmtVendor.id, params);
      if (format === 'pdf') {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `statement_${stmtVendor.firm_name}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        // show as JSON summary
        const summary = res.data?.summary;
        if (summary) {
          Modal.info({
            title: `Statement — ${stmtVendor.firm_name}`,
            width: 500,
            content: (
              <div style={{ fontSize: 13 }}>
                <div>Period: {res.data.date_from} → {res.data.date_to}</div>
                <div>Total Orders: {summary.total_orders}</div>
                <div>Total Billed: ₹{Number(summary.total_billed || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                <div>Total Paid: ₹{Number(summary.total_paid || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                <div>Outstanding: ₹{Number(summary.outstanding || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                <div>Credit Notes: ₹{Number(summary.total_credit_notes || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
              </div>
            ),
          });
        }
      }
    } catch (e) {
      message.error('Failed to generate statement');
    } finally {
      setStmtLoading(false);
    }
  };
  const { vendorScores } = useSelector((s) => s.vendorPerformance || {});

  const canViewPerf = hasPermission(permissions, 'purchase_order/view');

  const handleViewPerformance = (vendor) => {
    setPerfVendor(vendor);
    dispatch(fetchVendorPerformanceRequest(vendor.id));
  };

  // Check if user has permission to view the grid
  const canViewGrid = hasPermission(permissions, 'vendor/gridView');
  const canCreate = hasPermission(permissions, 'vendor/create');
  const canEdit = hasPermission(permissions, 'vendor/edit');

  useEffect(() => {
    if (canViewGrid) {
      dispatch(fetchVendors());
    }
  }, [dispatch, canViewGrid]);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  const handleEdit = (vendorId) => {
    navigate(`/vendors/edit/${vendorId}`);
  };

  // Filter vendors based on search
  const filteredVendors = vendors.filter(vendor =>
    vendor.firm_name?.toLowerCase().includes(searchText.toLowerCase()) ||
    vendor.contact_name?.toLowerCase().includes(searchText.toLowerCase()) ||
    vendor.mobile?.includes(searchText)
  );

  // If user doesn't have permission to view grid, show message
  if (!canViewGrid) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-state-icon"><LockOutlined /></div>
          <div className="empty-state-text">
            You don't have permission to view vendors.
          </div>
        </div>
      </div>
    );
  }

  const columns = [
    {
      title: 'Firm Name',
      dataIndex: 'firm_name',
      key: 'firm_name',
      width: 150,
      fixed: 'left',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <strong>{text}</strong>
        </Tooltip>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 155,
      ellipsis: true,
      render: (_, record) => (
        <div style={{ lineHeight: 1.4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <UserOutlined style={{ color: '#8c8c8c', fontSize: 11 }} />
            <span style={{ fontSize: 13 }}>{record.contact_name || '—'}</span>
          </div>
          {record.mobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
              <PhoneOutlined style={{ color: '#8c8c8c', fontSize: 11 }} />
              <span style={{ fontSize: 12, color: '#595959' }}>{record.mobile}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'GST / Udhyam',
      key: 'gst_udhyam',
      width: 165,
      render: (_, record) => (
        <div style={{ lineHeight: 1.5 }}>
          {record.gst_number
            ? <Tag color="blue" style={{ marginBottom: 2 }}>{record.gst_number}</Tag>
            : <span style={{ color: '#999', fontSize: 12 }}>No GST</span>}
          {record.udhyam_number && (
            <div><Tag color="green">{record.udhyam_number}</Tag></div>
          )}
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'udhyam_category',
      key: 'udhyam_category',
      width: 95,
      align: 'center',
      render: (text) => {
        if (!text) return <span style={{ color: '#bbb' }}>—</span>;
        const categoryColors = { micro: 'orange', small: 'cyan', medium: 'purple' };
        return <Tag color={categoryColors[text] || 'default'}>{text.toUpperCase()}</Tag>;
      },
    },
    ...(canViewPerf ? [{
      title: 'Score',
      key: 'perf_score',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const score = vendorScores?.[record.id];
        if (!score || score.total_orders === 0) {
          return (
            <Tooltip title="Click Performance to load score">
              <Tag color="default" style={{ fontSize: 11 }}>No data</Tag>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={`${Number(score.overall_score).toFixed(1)} / 100`}>
            {getGradeTag(score.score_grade)}
          </Tooltip>
        );
      },
    }] : []),
    ...(canViewPerf ? [{
      title: 'Perf.',
      key: 'performance',
      align: 'center',
      width: 75,
      render: (_, record) => (
        <Tooltip title="View vendor performance score">
          <Button
            size="small"
            icon={<TrophyOutlined />}
            onClick={() => handleViewPerformance(record)}
          />
        </Tooltip>
      ),
    }] : []),
    {
      title: 'Statement',
      key: 'statement',
      align: 'center',
      width: 90,
      render: (_, record) => (
        <Tooltip title="Download/View vendor statement">
          <Button
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => setStmtVendor({ id: record.id, firm_name: record.firm_name })}
          />
        </Tooltip>
      ),
    },
    ...(canEdit ? [{
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 75,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record.id)}
        >
          Edit
        </Button>
      ),
    }] : []),
  ];

  return (
    <div className="page-container">
      <div className="page-content">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-header-title">
            <div className="page-header-icon"><TeamOutlined /></div>
            <div className="page-header-text">
              <h1>Vendor Management</h1>
              <p>Manage your vendor relationships and contacts</p>
            </div>
          </div>
          <div className="page-header-actions">
            {canCreate && (
              <button
                className="btn btn-primary"
                onClick={() => navigate('/vendors/add')}
              >
                <PlusOutlined />
                Add New Vendor
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon"><BankOutlined /></div>
            <div className="stat-content">
              <div className="stat-value">{vendors.length}</div>
              <div className="stat-label">Total Vendors</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#17a2b8' }}>
            <div className="stat-icon" style={{ background: '#d1ecf1', color: '#0c5460' }}><CheckOutlined /></div>
            <div className="stat-content">
              <div className="stat-value">{vendors.filter(v => v.gst_number).length}</div>
              <div className="stat-label">GST Registered</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#28a745' }}>
            <div className="stat-icon" style={{ background: '#d4edda', color: '#155724' }}><FileProtectOutlined /></div>
            <div className="stat-content">
              <div className="stat-value">{vendors.filter(v => v.udhyam_number).length}</div>
              <div className="stat-label">With Udhyam</div>
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div className="modern-table-container">
          <div className="table-header">
            <h2 className="table-title">Vendors List</h2>
            <Search
              placeholder="Search vendors..."
              allowClear
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              size="large"
            />
          </div>
          <Table
            columns={columns}
            dataSource={filteredVendors}
            loading={loading}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} vendors`,
            }}
            locale={{
              emptyText: (
                <div className="empty-state">
                  <div className="empty-state-icon"><TeamOutlined /></div>
                  <div className="empty-state-text">No vendors found</div>
                  {canCreate && (
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate('/vendors/add')}
                    >
                      Add Your First Vendor
                    </button>
                  )}
                </div>
              ),
            }}
          />
        </div>
      </div>

      {/* Vendor Performance Modal */}
      {perfVendor && (
        <VendorPerformanceDetail
          vendorId={perfVendor.id}
          vendorName={perfVendor.firm_name}
          open={!!perfVendor}
          onClose={() => setPerfVendor(null)}
        />
      )}

      {/* Vendor Statement Modal */}
      <Modal
        title={<Space><FileTextOutlined />Statement — {stmtVendor?.firm_name}</Space>}
        open={!!stmtVendor}
        onCancel={() => { setStmtVendor(null); setStmtDateRange(null); }}
        footer={null}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Select a date range (default: last 30 days)
          </Typography.Text>
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            format="DD MMM YYYY"
            value={stmtDateRange}
            onChange={setStmtDateRange}
            allowClear
          />
        </div>
        <Space style={{ width: '100%', justifyContent: 'center' }}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={stmtLoading}
            onClick={() => handleDownloadStatement('pdf')}
          >
            Download PDF
          </Button>
          <Button
            icon={<EyeOutlined />}
            loading={stmtLoading}
            onClick={() => handleDownloadStatement('json')}
          >
            View Summary
          </Button>
        </Space>
      </Modal>
    </div>
  );
};

export default VendorList;